import { useState, useEffect } from "react"
import { Plus } from "lucide-react"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useListBoards, useCreateWidget } from "@/hooks/useBoard"
import { useToast } from "@/hooks/useToast"
import Loader from "@/components/ui/loader"
import { CreateBoardDialog } from "@/pages/dashboard/boards/components/create-board-dialog"

interface ExportToBoardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  widgetType: "table" | "chart" | "number" | "text"
  sqlQuery: string
  resourceId: string
  data?: any
  chartConfig?: { chartType: "bar" | "line" | "pie"; xAxis: string; yAxis: string }
  numberConfig?: { column: string; label?: string; fontSize?: number; color?: string }
}

export function ExportToBoardDialog({
  open,
  onOpenChange,
  widgetType,
  sqlQuery,
  resourceId,
  data,
  chartConfig,
  numberConfig,
}: ExportToBoardDialogProps) {
  const { toast } = useToast()
  const { data: boards, isLoading } = useListBoards()
  const createWidget = useCreateWidget()
  const [selectedBoardId, setSelectedBoardId] = useState("")
  const [widgetName, setWidgetName] = useState("")
  const [isCreateBoardOpen, setIsCreateBoardOpen] = useState(false)
  const [pendingBoardName, setPendingBoardName] = useState<string | null>(null)

  // Handle board creation success - select the newly created board
  const handleBoardCreated = (boardName: string) => {
    setPendingBoardName(boardName)
  }

  // Watch for boards to update and select the newly created board
  useEffect(() => {
    if (pendingBoardName && boards) {
      const newBoard = boards.find((b) => b.name === pendingBoardName)
      if (newBoard) {
        setSelectedBoardId(newBoard.id)
        setPendingBoardName(null)
      }
    }
  }, [boards, pendingBoardName])

  const handleExport = async () => {
    if (!selectedBoardId) {
      toast({
        description: "Please select a board",
        variant: "destructive",
      })
      return
    }

    if (!widgetName.trim()) {
      toast({
        description: "Widget name is required",
        variant: "destructive",
      })
      return
    }

    try {
      // Determine config based on widget type and data
      let config: Record<string, any> = {}

      if (widgetType === "chart" && data && Array.isArray(data) && data.length > 0) {
        if (chartConfig) {
          // Use the actual chart configuration from ChartTab
          if (chartConfig.chartType === "pie") {
            // Pie charts use category and value instead of xAxis/yAxis
            config = {
              chartType: chartConfig.chartType,
              category: chartConfig.xAxis || "",
              value: chartConfig.yAxis || "",
            }
          } else {
            // Bar and line charts use xAxis and yAxis
            config = {
              chartType: chartConfig.chartType,
              xAxis: chartConfig.xAxis || "",
              yAxis: chartConfig.yAxis || "",
            }
          }
        } else {
          // Fallback to default if chartConfig is not provided
          const keys = Object.keys(data[0])
          config = {
            chartType: "bar",
            xAxis: keys[0] || "",
            yAxis: keys[1] || "",
          }
        }
      } else if (widgetType === "number" && data && Array.isArray(data) && data.length > 0) {
        if (numberConfig) {
          // Use the number configuration from NumberTab
          config = {
            valueKey: numberConfig.column || "",
            label: numberConfig.label || undefined,
            fontSize: numberConfig.fontSize || undefined,
            color: numberConfig.color || undefined,
          }
        } else {
          // Fallback to default if numberConfig is not provided
          const keys = Object.keys(data[0])
          config = {
            valueKey: keys[0] || "",
          }
        }
      }

      await createWidget.mutateAsync({
        boardId: selectedBoardId,
        name: widgetName.trim(),
        widget_type: widgetType,
        sql_query: sqlQuery,
        resource_id: resourceId,
        config,
        position: 0,
      })

      toast({
        description: "Widget exported to board successfully",
      })
      onOpenChange(false)
      setSelectedBoardId("")
      setWidgetName("")
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to export widget",
        variant: "destructive",
      })
    }
  }

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedBoardId("")
      setWidgetName("")
      setIsCreateBoardOpen(false)
      setPendingBoardName(null)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export to Board</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader />
            </div>
          ) : (
            <>
              <div>
                <Label htmlFor="board">Board</Label>
                <Select
                  value={selectedBoardId}
                  onValueChange={(value) => {
                    if (value === "__create_new__") {
                      setIsCreateBoardOpen(true)
                    } else {
                      setSelectedBoardId(value)
                    }
                  }}
                >
                  <SelectTrigger id="board">
                    <SelectValue placeholder="Select a board" />
                  </SelectTrigger>
                  <SelectContent>
                    {boards?.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        {board.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__create_new__" className="text-primary">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Create New Board
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="widget-name">Widget Name</Label>
                <Input
                  id="widget-name"
                  value={widgetName}
                  onChange={(e) => setWidgetName(e.target.value)}
                  placeholder="Enter widget name"
                  autoFocus
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={createWidget.isPending || !selectedBoardId || !widgetName.trim()}>
            {createWidget.isPending ? "Exporting..." : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <CreateBoardDialog open={isCreateBoardOpen} onOpenChange={setIsCreateBoardOpen} onSuccess={handleBoardCreated} />
    </Dialog>
  )
}
